// Dar ekran yan paneli (overlay) kapatma + odak devri — Finar UX P3 Ö6: ≤1024 px'te FormFill
// yan panelleri formu itmeden üstüne açılır.
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Escape (onEscape) ya da panel dışına tıklama (onOutside) ile kapatır. Üstte açık bir modal
 * (aria-modal) varken ya da Escape'i başka bir dinleyici işlediyse (defaultPrevented) Escape
 * paneli kapatmaz — tek tuş iki katmanı birden kapatmasın.
 */
export function useOverlayDismiss(
  ref: RefObject<HTMLElement>,
  active: boolean,
  onOutside: () => void,
  onEscape: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      onEscape();
    }
    function onDown(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [ref, active, onOutside, onEscape]);
}

/**
 * Daraltılabilir panelin odak devri: "…aç" düğmesi panel açılınca DOM'dan çıkar, "…daralt"
 * düğmesi kapanınca çıkar — odak BODY'ye düşmesin. Yalnız kullanıcı eylemiyle (markOpen /
 * markClose) çalışır; kırılım değişimiyle kendiliğinden daralma/açılma odağı çalmaz. Dış
 * tıklamayla kapanışta işaret konmaz — odak tıklanan öğede kalır.
 */
export function usePanelFocus(
  collapsed: boolean,
  panelRef: RefObject<HTMLElement>,
  openerRef: RefObject<HTMLElement>,
): { markOpen: () => void; markClose: () => void } {
  const pending = useRef<"panel" | "opener" | null>(null);
  useEffect(() => {
    const target = pending.current;
    pending.current = null;
    if (target === "panel" && !collapsed) {
      const panel = panelRef.current;
      (panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel)?.focus();
    } else if (target === "opener" && collapsed) {
      openerRef.current?.focus();
    }
  }, [collapsed, panelRef, openerRef]);
  return {
    markOpen: () => {
      pending.current = "panel";
    },
    markClose: () => {
      pending.current = "opener";
    },
  };
}

/** Overlay kipinde açık panelin yerinde kalan 48 px'lik yer tutucu + üstte panel kutusu. */
export const OVERLAY_PANEL_STYLE = {
  position: "absolute",
  top: 0,
  bottom: 0,
  zIndex: 20,
  boxShadow: "var(--shadow-md)",
} as const;
